import React, { useState, useEffect } from "react";
import * as serviciosApiService from '@/services/serviciosApiService';
import * as sucursalesApiService from '@/services/sucursalesApiService';
import * as categoriasApiService from "@/services/categoriasApiService";
import Modal from "@/components/common/Modal";
import Button from "@/components/common/Button";
import FilterBar from "@/components/common/FilterBar";
import CustomSelect from "@/components/common/CustomSelect";
import CustomCheckbox from "@/components/common/CustomCheckbox";
import CheckboxFiltersContainer from "@/components/common/CheckboxFiltersContainer";
import ServiciosList from "./ServiciosList";
import ServicioForm from './ServicioForm';
import { FaPlus, FaFilter, FaSearch, FaMapMarkerAlt } from 'react-icons/fa';
import toastService from '@/utils/toastService';
import { useSucursal } from '@/context/SucursalContext';

/**
 * Componente principal para la gestión de servicios
 * 
 * @returns {JSX.Element} - Componente de mantenedor de servicios
 */
const ServiciosMantenedor = () => {
  const [servicios, setServicios] = useState([]);
  const [sucursales, setSucursales] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [filteredServicios, setFilteredServicios] = useState([]);
  const [selectedServicio, setSelectedServicio] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // Filtros
  const [filters, setFilters] = useState({
    categoria: '',
    searchTerm: '',
    mostrarInactivos: false
  });
  
  // Estado para detectar si estamos en móvil
  const [isMobile, setIsMobile] = useState(false);
  
  // Detectar si estamos en móvil
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    // Comprobar inicialmente
    checkMobile();
    
    // Actualizar cuando cambie el tamaño de la ventana
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const { selectedSucursal } = useSucursal();
  
  // Cargar categorías de servicios desde la API
  const fetchCategorias = async () => {
    try {
      const categoriasData = await categoriasApiService.getCategoriasByTipo('categoria_servicio');
      setCategorias(categoriasData);
    } catch (error) {
      console.error('Error al cargar categorías:', error);
      toastService.error('Error al cargar categorías');
    }
  };

  // Aplicar filtros cuando cambian
  useEffect(() => {
    applyFilters();
  }, [servicios, filters]);

  // Cargar datos iniciales
  useEffect(() => {
    fetchCategorias();
  }, []);

  // Aplicar filtros a la lista de servicios
  const applyFilters = () => {

    let filtered = [...servicios];
    
    // Filtrar por categoría
    if (filters.categoria) {
      filtered = filtered.filter(s => s.Categoria === filters.categoria);
    }
    
    // Filtrar por término de búsqueda
    if (filters.searchTerm) {
      const term = filters.searchTerm.toLowerCase();
      filtered = filtered.filter(s => 
        s.Nombre.toLowerCase().includes(term) || 
        (s.Descripcion && s.Descripcion.toLowerCase().includes(term))
      );
    }
    
    // Filtrar por estado activo/inactivo (filtrado frontend instantáneo)

    if (!filters.mostrarInactivos) {

      const beforeFilter = filtered.length;
      filtered = filtered.filter(s => {

        return s.Disponible === true;
      });

    } else {

    }
    
    setFilteredServicios(filtered);
  };

  // Cargar servicios y sucursales al iniciar
  useEffect(() => {

    fetchData();
  }, [selectedSucursal]);
  
  // Aplicar filtros cuando cambian los servicios o filtros
  useEffect(() => {
    applyFilters();
  }, [servicios, filters]);

  // Función para cargar datos desde la API
  const fetchData = async () => {
    setLoading(true);
    try {


      // Cargar sucursales
      try {
        const sucursalesData = await sucursalesApiService.getSucursales();
        setSucursales(sucursalesData);

      } catch (sucErr) {
        console.error("Error al cargar sucursales:", sucErr);
      }
      
      if (selectedSucursal) {

        
        // Obtener todos los servicios (activos e inactivos) para filtrado frontend - como mecánicos
        const serviciosData = await serviciosApiService.getServicios(selectedSucursal);

        setServicios(serviciosData);
      } else {

        setServicios([]);
        // toastService.warning("Selecciona una sucursal para ver los servicios"); // Removido: molesto al refrescar
      }
    } catch (error) {
      console.error("Error al cargar datos:", error);
      toastService.error("Error al cargar los datos");
    } finally {
      setLoading(false);
    }
  };

  // Manejar cambios en los filtros
  const handleFilterChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Abrir modal para agregar o editar servicio
  const handleOpenModal = (servicio = null) => {
    setSelectedServicio(servicio);
    setIsModalOpen(true);
  };

  // Cerrar modal
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedServicio(null);
  };

  // Manejar envío del formulario
  const handleSubmitForm = async (formData) => {
    try {
      if (selectedServicio) {
        // Actualizar servicio existente
        await serviciosApiService.updateServicio(selectedServicio.id, formData);
        toastService.success("Servicio actualizado correctamente");
      } else {
        // Crear nuevo servicio
        await serviciosApiService.createServicio(formData);
        toastService.success("Servicio creado correctamente");
      }
      
      // Recargar la lista de servicios
      fetchData();
      
      // Cerrar el modal
      handleCloseModal();
    } catch (error) {
      console.error("Error al guardar servicio:", error);
      toastService.error("Error al guardar el servicio");
    }
  };

  // Manejar eliminación de servicio
  const handleDeleteServicio = async (id) => {
    try {
      await serviciosApiService.deleteServicio(id);
      // Recargar la lista de servicios
      fetchData();
      toastService.success("Servicio eliminado correctamente");
    } catch (error) {
      console.error("Error al eliminar servicio:", error);
      toastService.error("Error al eliminar el servicio");
    }
  };

  return (
    <div className="p-4 md:p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Servicios</h1>
        
        <Button
          variant="primary"
          onClick={() => handleOpenModal()}
          icon={<FaPlus />}
        >
          Agregar Servicio
        </Button>
      </div>
      
      {/* Filtros con el componente FilterBar mejorado */}
      <FilterBar 
        onSearch={(value) => setFilters({...filters, searchTerm: value})}
        searchValue={filters.searchTerm}
        onReset={() => setFilters({categoria: '', searchTerm: '', mostrarInactivos: false})}
        searchPlaceholder="Buscar servicios..."
        checkboxFilters={
          <CheckboxFiltersContainer>
            <CustomCheckbox
              checked={filters.mostrarInactivos}
              onChange={(checked) => {
                setFilters(prev => ({
                  ...prev,
                  mostrarInactivos: checked
                }));
              }}
              label="Incluir inactivos"
              id="mostrarInactivos"
              name="mostrarInactivos"
            />
          </CheckboxFiltersContainer>
        }
      >
        {/* Filtro por categoría - CustomSelect para consistencia en altura */}
        <CustomSelect
          name="categoria"
          value={filters.categoria}
          onChange={handleFilterChange}
          className="w-full md:min-w-[200px] md:w-auto bg-gray-700 border border-gray-600 rounded text-white"
          placeholder="Todas las categorías"
          options={categorias
            .sort((a, b) => a.nombre.localeCompare(b.nombre))
            .map(cat => ({
              id: cat.id,
              value: cat.id.toString(),
              label: cat.nombre
            }))}
        />
      </FilterBar>

      {/* Mostrar cargando o lista de servicios */}
      {loading ? (
        <div className="flex justify-center py-6">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      ) : (
        <ServiciosList
          servicios={filteredServicios}
          sucursales={sucursales}
          categorias={categorias}
          onEdit={handleOpenModal}
          onDelete={handleDeleteServicio}
        />
      )}

      {/* Modal para agregar o editar servicio */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={selectedServicio ? 'Editar Servicio' : 'Nuevo Servicio'}
        size="2xl"
        headerExtra={selectedSucursal && (
          <div className="bg-blue-900/30 px-3 py-2 rounded-lg border border-blue-800/50">
            <p className="text-blue-200 text-sm flex items-center">
              <FaMapMarkerAlt className="text-blue-400 mr-2 text-sm" />
              <strong>{sucursales.find(s => s.id === selectedSucursal)?.Nombre || 'Desconocida'}</strong>
            </p>
          </div>
        )}
      >
        <ServicioForm
          initialData={selectedServicio || {}}
          sucursales={sucursales}
          categorias={categorias}
          onSubmit={handleSubmitForm}
          onCancel={handleCloseModal}
          onStatusChange={fetchData}
        />
      </Modal>
    </div>
  );
};

export default ServiciosMantenedor;